import { Box, HStack, Input, Link, Spacer, Stack, Table, Text } from '@chakra-ui/react'
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical'
import type { FC } from 'react'
import { useRef } from 'react'
import { useParams } from 'react-router-dom'

import { ClipboardRoot, ClipboardInput, ClipboardIconButton } from '@app/components/ui/clipboard'
import { InputGroup } from '@app/components/ui/input-group'
import useGetObject from '@app/hooks/useGetObject'
import LabelsInput from '@app/next/forms/inline/labelsInput'
import RichTextInput from '@app/next/forms/inline/richTextInput'
import ViewerFieldWrapper from '@app/next/forms/inline/support/viewerFieldWrapper'
import InlineUpdaterRoot from '@app/next/forms/inlineUpdaterRoot'
import UrlInput from '@app/next/forms/urlInput'
import OwnerPopover from '@app/next/ownerPopover'
import AiPopulateMetricDescriptionButton from '@app/pages/metrics/components/aiPopulateMetricDescriptionButton'
import AiPopulateTechnicalDescriptionButton from '@app/pages/metrics/components/aiPopulateTechnicalDescriptionButton'
import MetricGraph from '@app/pages/metrics/components/metricGraph'
import DetailsTable from '@app/pages/shared/detailsTable'
import CardStats from '@app/shared/cards/metric/cardStats'

const MetricDetails: FC = () => {
  const { nodeId, strategyId } = useParams()
  const metric = useGetObject(nodeId, 'metric')
  const { externalUrl, externalUrlTitle, slug } = metric
  const descriptionRef = useRef(null)
  const technicalDescriptionRef = useRef(null)

  const setEditorValue = (ref, value) => {
    const editorInstance = ref?.current

    if (!editorInstance || !value) {
      return
    }

    editorInstance.update(() => {
      const root = $getRoot()
      root.clear()

      const paragraph = $createParagraphNode()
      const text = $createTextNode(value)
      paragraph.append(text)
      root.append(paragraph)
    })
  }

  return (
    <Stack gap={4}>
      <MetricGraph metric={metric} />
      {strategyId && (
        <Box px={4}>
          <CardStats strategyId={strategyId} metric={metric} />
        </Box>
      )}
      <Stack gap={4} px={2}>
        <DetailsTable>
          <Table.Body>
            <Table.Row>
              <Table.Cell fontWeight="semibold">
                <Text>ID</Text>
              </Table.Cell>
              <Table.Cell>
                <ClipboardRoot value={slug}>
                  <InputGroup width="full" endElement={<ClipboardIconButton me="-2" />}>
                    <ClipboardInput readOnly value={slug} />
                  </InputGroup>
                </ClipboardRoot>
              </Table.Cell>
            </Table.Row>
            <ViewerFieldWrapper value={metric.description}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">
                  <HStack>
                    Description
                    <Spacer />
                    <AiPopulateMetricDescriptionButton
                      metricId={metric.id}
                      colorPalette="purple"
                      size="xs"
                      onSuccess={(result) => setEditorValue(descriptionRef, result)}
                    />
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <RichTextInput
                    domainObject={metric}
                    name="description"
                    placeholder="Description..."
                    ref={descriptionRef}
                  />
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
            <ViewerFieldWrapper value={metric.technicalDescription}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">
                  <HStack>
                    Technical Description
                    <Spacer />
                    <AiPopulateTechnicalDescriptionButton
                      metricId={metric.id}
                      colorPalette="purple"
                      size="xs"
                      onSuccess={(result) => setEditorValue(technicalDescriptionRef, result)}
                    />
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <RichTextInput
                    domainObject={metric}
                    name="technicalDescription"
                    placeholder="Technical description..."
                    ref={technicalDescriptionRef}
                  />
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
            <ViewerFieldWrapper value={!!externalUrl}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">
                  {externalUrl ? (
                    <Link
                      color="link"
                      fontSize="sm"
                      whiteSpace="no"
                      wordBreak="break-all"
                      href={externalUrl}
                      rel="onoopener noreferrer"
                      target="_blank"
                    >
                      External URL
                    </Link>
                  ) : (
                    <Text>External URL</Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <InlineUpdaterRoot domainObject={metric}>
                    <UrlInput defaultValue={externalUrl || ''} name="externalUrl" placeholder="External URL..." />
                  </InlineUpdaterRoot>
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
            <ViewerFieldWrapper value={metric.externalUrlTitle}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">External URL title</Table.Cell>
                <Table.Cell>
                  <InlineUpdaterRoot domainObject={metric}>
                    <Input defaultValue={externalUrlTitle || ''} name="externalUrlTitle" placeholder="See more..." />
                  </InlineUpdaterRoot>
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
            <ViewerFieldWrapper value={metric.owner}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Owner</Table.Cell>
                <Table.Cell>
                  <OwnerPopover object={metric} showName />
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
            <ViewerFieldWrapper value={metric.labels}>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Labels</Table.Cell>
                <Table.Cell>
                  <LabelsInput domainObject={metric} name="labels" />
                </Table.Cell>
              </Table.Row>
            </ViewerFieldWrapper>
          </Table.Body>
        </DetailsTable>
      </Stack>
    </Stack>
  )
}

export default MetricDetails
